import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, Loader2, ArrowLeft, Filter, Calendar, DollarSign, User, Mail } from 'lucide-react';
import { toast } from 'sonner';
import MobileHeader from '../components/MobileHeader';

const ApprovalsPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [event, setEvent] = useState(null);
  const [pendingContributions, setPendingContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  useEffect(() => {
    if (eventId && token) {
      fetchEventAndContributions();
    }
  }, [eventId, token]);

  const fetchEventAndContributions = async () => {
    setLoading(true);
    try {
      // Fetch event details
      const eventResponse = await axios.get(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvent(eventResponse.data);

      // Fetch pending contributions
      const contributionsResponse = await axios.get(`${API}/events/${eventId}/pending-contributions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Backend returns { event, pending_contributions, total_pending, total_pending_amount }
      const data = contributionsResponse.data;
      if (data && Array.isArray(data.pending_contributions)) {
        setPendingContributions(data.pending_contributions);
      } else {
        setPendingContributions([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load contributions');
      setPendingContributions([]); // Set to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (rsvpId) => {
    setProcessing(prev => ({ ...prev, [rsvpId]: 'approving' }));
    try {
      await axios.put(
        `${API}/events/${eventId}/verify-contribution/${rsvpId}`,
        { status: 'verified' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Contribution approved successfully!');
      fetchEventAndContributions();
    } catch (error) {
      console.error('Error approving contribution:', error);
      toast.error('Failed to approve contribution');
    } finally {
      setProcessing(prev => ({ ...prev, [rsvpId]: null }));
    }
  };

  const handleReject = async (rsvpId) => {
    setProcessing(prev => ({ ...prev, [rsvpId]: 'rejecting' }));
    try {
      await axios.put(
        `${API}/events/${eventId}/verify-contribution/${rsvpId}`,
        { status: 'rejected' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Contribution rejected');
      fetchEventAndContributions();
    } catch (error) {
      console.error('Error rejecting contribution:', error);
      toast.error('Failed to reject contribution');
    } finally {
      setProcessing(prev => ({ ...prev, [rsvpId]: null }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pb-20">
        <MobileHeader title="Approvals" />
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-blue-900 mx-auto mb-4" />
          <p className="text-gray-600">Loading contributions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <MobileHeader title="Approvals" />
      
      <div className="pt-16 px-4 max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/host-dashboard')}
          className="mb-4 text-blue-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Event Info Header */}
        {event && (
          <Card className="mb-6 shadow-md-2 border-0 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white p-4">
              <h2 className="text-xl font-bold mb-1">{event.title}</h2>
              <p className="text-sm text-blue-100">{event.location}</p>
            </div>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="text-center flex-1">
                  <p className="text-2xl font-bold text-gray-900">{pendingContributions.length}</p>
                  <p className="text-xs text-gray-600">Pending</p>
                </div>
                <div className="h-10 w-px bg-gray-300" />
                <div className="text-center flex-1">
                  <p className="text-2xl font-bold text-green-600">
                    {event.contribution_goals?.reduce((sum, g) => sum + (g.current_amount || 0), 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-600">Raised (₹)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Contributions List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              Pending Approvals ({pendingContributions.length})
            </h3>
          </div>

          {pendingContributions.length === 0 ? (
            <Card className="shadow-md-1">
              <CardContent className="py-12 text-center">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4 opacity-50" />
                <p className="text-gray-600 font-medium">No pending contributions</p>
                <p className="text-sm text-gray-500 mt-2">All contributions have been reviewed</p>
              </CardContent>
            </Card>
          ) : (
            Array.isArray(pendingContributions) && pendingContributions.map((rsvp) => (
              <Card key={rsvp.id} className="shadow-md-2 border-0 overflow-hidden">
                <CardHeader className="bg-gray-50 border-b pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-base font-bold text-gray-900 flex items-center gap-2">
                        <User className="h-4 w-4 text-blue-900" />
                        {rsvp.guest_name}
                      </CardTitle>
                      <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {rsvp.guest_email}
                      </p>
                    </div>
                    <Badge variant={rsvp.payment_status === 'paid' ? 'success' : 'warning'}>
                      {rsvp.payment_status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  {/* Contributions by Goal */}
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700">Contributions:</p>
                    {rsvp.contributions?.map((contrib, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                        <span className="text-sm text-gray-700">{contrib.goal_title}</span>
                        <span className="font-bold text-blue-900">₹{contrib.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>

                  {/* Total Amount */}
                  <div className="flex items-center justify-between pt-3 border-t">
                    <span className="font-semibold text-gray-900">Total Amount</span>
                    <span className="text-xl font-bold text-green-600">
                      ₹{rsvp.total_contribution_amount?.toLocaleString() || 0}
                    </span>
                  </div>

                  {/* Transaction ID */}
                  {rsvp.transaction_id && (
                    <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Transaction ID</p>
                      <p className="font-mono text-sm font-semibold text-gray-900 break-all">
                        {rsvp.transaction_id}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={() => handleApprove(rsvp.id)}
                      disabled={!!processing[rsvp.id]}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white h-12 shadow-md-1 active:scale-95 transition-transform"
                    >
                      {processing[rsvp.id] === 'approving' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => handleReject(rsvp.id)}
                      disabled={!!processing[rsvp.id]}
                      variant="outline"
                      className="flex-1 border-red-500 text-red-600 hover:bg-red-50 h-12 shadow-md-1 active:scale-95 transition-transform"
                    >
                      {processing[rsvp.id] === 'rejecting' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ApprovalsPage;
